import { TextNode } from '@ravago/shared/page-data/models/elements';

import { parse } from 'node-html-parser';

export interface Link {
   href: string;
   target: '_blank' | '_self';
   title: string;
}

const HEADER_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5'];
const BODY_TAGS = ['p'];
const LIST_TAGS = ['ol', 'ul'];

const HEADER_CLASSES = 'typography-container-heading';
const BODY_CLASSES = 'typography-container';

const VARIANT_CLASSES: Record<string, string> = {
   h1: 'phone:typography-component-data-display-typography-mobile-h1 tab-port:typography-component-data-display-typography-tablet-h1 tab-land:typography-component-data-display-typography-tablet-h1 typography-component-data-display-typography-desktop-h1',
   h2: 'phone:typography-component-data-display-typography-mobile-h2 tab-port:typography-component-data-display-typography-tablet-h2 tab-land:typography-component-data-display-typography-tablet-h2 typography-component-data-display-typography-desktop-h2',
   h3: 'phone:typography-component-data-display-typography-mobile-h3 tab-port:typography-component-data-display-typography-tablet-h3 tab-land:typography-component-data-display-typography-tablet-h3 typography-component-data-display-typography-desktop-h3',
   h4: 'phone:typography-component-data-display-typography-mobile-h4 tab-port:typography-component-data-display-typography-tablet-h4 tab-land:typography-component-data-display-typography-tablet-h4 typography-component-data-display-typography-desktop-h4',
   p: 'phone:typography-component-data-display-typography-mobile-body-regular tab-port:typography-component-data-display-typography-tablet-body-regular tab-land:typography-component-data-display-typography-tablet-body-regular typography-component-data-display-typography-desktop-body-regular'
};

export class TextUtils {
   private static normalizeHtmlFragment(html: string): string {
      const root = parse(html);
      const documentElements = root.querySelectorAll('html, head, body');

      if (!documentElements.length) return html;

      for (const element of documentElements.reverse()) {
         if (element.tagName === 'HEAD') {
            element.remove();
         } else {
            element.replaceWith(...element.childNodes);
         }
      }

      return root.innerHTML;
   }

   /**
    * Adds classes to the html string and replaces the tag with a new tag if provided
    *
    * @param value the html string to replace the tag in
    * @param tag the tag to replace
    *
    * @returns the html string with the tag replaced
    */
   private static replaceHtml(value: string, tag: string) {
      const isHeaderTag = HEADER_TAGS.includes(tag);

      const variantClass = VARIANT_CLASSES[tag];

      const classString = isHeaderTag ? HEADER_CLASSES : BODY_CLASSES;

      return (
         value
            // First replace existing class attribute to prevent class duplication
            .replaceAll(`<${tag} class="`, `<${tag} class="${classString} ${variantClass} `) // Trailing space is important to prevent class from merging into each other
            // Then add class attribute if not present
            .replaceAll(`<${tag}>`, `<${tag} class="${classString} ${variantClass}">`)
      );
   }

   /**
    * Adds classes to the html string and replaces the tag with a new tag if provided
    *
    * @param html the html string to add classes to
    * @param newTag the tag to replace the html tag with
    *
    * @returns the html string with classes added
    */
   static addClasses(html: string) {
      return [...HEADER_TAGS, ...BODY_TAGS, ...LIST_TAGS].reduce((acc, tag) => this.replaceHtml(acc, tag), html);
   }

   /**
    * Gets the text in html format to render for the TextNode
    *
    * @param node the TextNode to get the text from
    * @param isConsumer whether the text is for the consumer or not
    * @param link the link to add to the text
    *
    * @returns the text in html format
    */
   static getRenderText(node: TextNode, isConsumer: boolean, link?: Link): string {
      const html = this.normalizeHtmlFragment(node.html.value ?? '');

      if (!html.trim().replace(/<[^>]*>?/gm, '').length && !isConsumer) return '<p>Lorem ipsum dolor sit...</p>';

      const htmlWithClasses = this.addClasses(html);

      if (!link?.href) return htmlWithClasses;

      return `<a href="${link.href}" target="${link.target}" title="${link.title}">${htmlWithClasses}</a>`;
   }
}
